import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export type AlertType = "success" | "info" | "warning" | "error";

export interface AlertRecord {
  id: number;
  title: string;
  message: string;
  type: AlertType;
}

interface AlertsContextValue {
  alerts: AlertRecord[];
  addAlert: (title: string, message: string, type?: AlertType) => number;
  removeAlert: (id: number) => void;
  clearAlerts: () => void;
}

const AlertsContext = createContext<AlertsContextValue | null>(null);

export const AlertsContextWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [alerts, setAlerts] = useState<AlertRecord[]>([]);

  const addAlert = useCallback<
    AlertsContextValue["addAlert"]
  >((title, message, type = "info") => {
    const id = Date.now() + Math.random();
    setAlerts((current) => [...current, { id, title, message, type }]);
    return id;
  }, []);

  const removeAlert = useCallback<AlertsContextValue["removeAlert"]>((id) => {
    setAlerts((current) => current.filter((alert) => alert.id !== id));
  }, []);

  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  const value = useMemo(
    () => ({
      alerts,
      addAlert,
      removeAlert,
      clearAlerts,
    }),
    [addAlert, alerts, clearAlerts, removeAlert]
  );

  return (
    <AlertsContext.Provider value={value}>{children}</AlertsContext.Provider>
  );
};

export const useAlertsContext = () => {
  const context = useContext(AlertsContext);

  if (!context) {
    throw new Error(
      "useAlertsContext must be used within an AlertsContextWrapper"
    );
  }

  return context;
};

